/*
 * File: ErrorBoundary.js
 * Path: src/components/ErrorBoundary/
 * Purpose: Enhanced error boundary with detailed error logging
 * Created: 2025-08-11
 * Dependencies: React, Sentry (optional)
 */

import React from 'react';
import PropTypes from 'prop-types';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      componentStack: ''
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Log to error tracking service (e.g., Sentry) if available
    if (window.Sentry) {
      window.Sentry.captureException(error, { extra: errorInfo });
    }

    this.setState({
      error,
      errorInfo,
      componentStack: errorInfo?.componentStack || ''
    });
  }

  renderErrorDetails() {
    if (!this.state.error) return null;
    
    return (
      <div style={{ padding: '20px', color: '#721c24', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
        <h3>Error Details:</h3>
        <p><strong>Message:</strong> {this.state.error.message}</p>
        <details style={{ marginTop: '10px' }}>
          <summary>Stack Trace</summary>
          <pre style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f1f1f1', padding: '10px', borderRadius: '4px' }}>
            {this.state.error.stack}
          </pre>
        </details>
        {this.state.componentStack && (
          <details style={{ marginTop: '10px' }}>
            <summary>Component Stack</summary>
            <pre style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f1f1f1', padding: '10px', borderRadius: '4px' }}>
              {this.state.componentStack}
            </pre>
          </details>
        )}
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary" style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
          <h2 style={{ color: '#dc3545' }}>Something went wrong</h2>
          <p>We're sorry for the inconvenience. The error has been logged.</p>
          {process.env.NODE_ENV === 'development' && this.renderErrorDetails()}
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
